// LoginPage.js

import React, { useState } from 'react'; 
import './LoginPage.css';
import { useNavigate } from 'react-router-dom'; 
import { signIn, signInWithMFA } from '../services/supabaseClient';
import { getUserDetails } from '../services/userInfoService';
import { getMerchantInfo } from '../services/paymentsService';
import newtonWordmark from '../assets/newton-wordmark.svg';
import {
    persistPracticeSelection,
    pickLoginPracticeId,
    loadEnabledPractices,
} from '../lib/practiceAccess';

// Password-reveal glyphs (lucide eye / eye-off, ISC licence), inlined so
// the login page needs nothing beyond its own CSS.
const eyeSvgProps = {
    width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
    strokeWidth: 1.75, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true,
};
const EyeIcon = () => (
    <svg {...eyeSvgProps}>
        <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);
const EyeOffIcon = () => (
    <svg {...eyeSvgProps}>
        <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
        <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
        <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
        <path d="m2 2 20 20" />
    </svg>
);

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [mfaCode, setMfaCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [requiresMFA, setRequiresMFA] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');

        try {
            // If we're already in MFA mode, verify the MFA code
            if (requiresMFA) {
                console.log('Verifying MFA code:', mfaCode);
                try {
                    const result = await signInWithMFA(mfaCode);
                    console.log('MFA verification result:', result);
                    
                    if (result.session && result.requiresMFA === false) {
                        // MFA verification successful, proceed with login
                        console.log('MFA verification successful, calling completeLogin');
                        await completeLogin(result.session, result.user);
                        return;
                    } else {
                        console.log('MFA verification failed or still requires MFA:', result);
                        setError('Invalid 2FA code. Please try again.');
                        setLoading(false);
                        return;
                    }
                } catch (mfaError) {
                    console.error('MFA verification threw an error:', mfaError);
                    setError(mfaError.message || 'Invalid 2FA code. Please try again.');
                    setLoading(false);
                    return;
                }
            }

            // Initial password authentication
            const result = await signIn(email, password);
            
            if (result.requiresMFA) {
                // User has MFA enabled, show MFA input
                console.log('MFA required. AAL levels:', { current: result.currentAAL, required: result.requiredAAL });
                setRequiresMFA(true);
                setLoading(false);
                setError('');
                return;
            } else if (result.session) {
                // No MFA required, proceed with login
                await completeLogin(result.session, result.user);
                return;
            } else {
                setError('Login failed. Please try again.');
                setLoading(false);
            }
        } catch (error) {
            // Handle any unexpected errors during the sign-in process
            console.error('Error logging in:', error);
            if (error.message === 'Email not confirmed') {
                setError('Please confirm your email before logging in.');
            } else if (error.message.includes('Invalid login credentials')) {
                setError('Invalid email or password.');
            } else if (error.message.includes('Invalid TOTP code') || error.message.includes('Invalid 2FA code')) {
                setError(error.message);
            } else if (error.message.includes('TOTP code expired')) {
                setError('2FA code has expired. Please use a fresh code from your authenticator app.');
            } else if (error.message.includes('MFA code must be exactly 6 digits')) {
                setError('Please enter a valid 6-digit code from your authenticator app.');
            } else if (error.message.includes('No verified MFA factor found')) {
                setError('2FA is not properly set up. Please contact support.');
            } else {
                setError(error.message || 'An unexpected error occurred. Please try again.');
            }
            setLoading(false);
        }
    };

    const completeLogin = async (session, user) => {
        try {
            console.log('Starting completeLogin with:', { session: !!session, user: !!user });
            
            // If sign-in is successful, store the JWT token in local storage
            if (session && session.access_token) {
                localStorage.setItem('jwtToken', session.access_token);
                localStorage.setItem('refreshToken', session.refresh_token);
                console.log("user object ", user)
                
                // Fetch user details here - use the user ID from the user object, not email
                const userId = email; // Use email as identifier for getUserDetails
                console.log("Using userId for getUserDetails:", userId)
                console.log("Full user object:", user)
                
                try {
                    const details = await getUserDetails(userId);
                    console.log('User Details:', details);
                    
                    const selectable = await loadEnabledPractices();
                    const practiceId = pickLoginPracticeId(selectable, details);
                    localStorage.setItem('practiceId', practiceId);
                    localStorage.setItem('bAdminUser', details.bAdminUser);
                    localStorage.setItem('bGroupAdmin', details.bGroupAdmin);
                    localStorage.setItem('groupAdminOrg', details.groupAdminOrg);
                    localStorage.setItem('bNonVoipOnboarding', details.bNonVoipOnboarding);
                    localStorage.setItem('allowedPractices', JSON.stringify(details.allowedPractices));
                    localStorage.setItem('email', email);
                    localStorage.setItem('useremail', email);
                    persistPracticeSelection(practiceId, email);

                    // Prime the merchant-status cache so the Dashboard can
                    // render the sidebar (Merchant tab pinned-to-top vs.
                    // legacy 4th-row, default tab on login) on first paint
                    // without waiting on a round-trip. The Dashboard does a
                    // background refresh on mount, and MerchantTab keeps
                    // this in sync whenever it loads, so the cache is
                    // self-healing once the practice finishes onboarding.
                    try {
                        const merchantData = await getMerchantInfo(practiceId);
                        const merchantStatus = merchantData?.merchant_status === 'found'
                            ? merchantData?.merchant?.merchant_status
                            : merchantData?.merchant_status; // 'not_found' or legacy shape
                        if (merchantStatus) {
                            localStorage.setItem('merchantStatus', merchantStatus);
                        } else {
                            localStorage.removeItem('merchantStatus');
                        }
                    } catch (merchantErr) {
                        console.warn('Failed to prime merchantStatus cache:', merchantErr);
                        localStorage.removeItem('merchantStatus');
                    }

                    // Reset loading state and navigate to the payments dashboard
                    setLoading(false);
                    console.log('User signed in', user, session);
                    console.log("Practice ID:", practiceId)
                    
                    // Navigate to payments dashboard or home page
                    navigate('/dashboard');
                } catch (error) {
                    console.error('Error fetching user details:', error);
                    setError('Error fetching user details: ' + error.message);
                    setLoading(false);
                }

            } else {
                console.error('Session is undefined, unable to retrieve JWT token');
                setError('Login successful, but unable to retrieve session information.');
                setLoading(false);
            }
        } catch (error) {
            console.error('Error in completeLogin:', error);
            setError('Login completion failed: ' + error.message);
            setLoading(false);
        }
    };

    return (
        <div className="newton-login">
            {/* Left — editorial panel (same as the main Newton app's login) */}
            <section className="newton-login__editorial">
                {/* Wordmark lockup — "Newton | Pay", so it's clear which
                    Newton product this sign-in is for. */}
                <div className="newton-login__lockup">
                    <img src={newtonWordmark} alt="Newton" className="newton-login__wordmark" />
                    <span className="newton-login__lockup-divider" aria-hidden="true" />
                    <span className="newton-login__lockup-product">Pay</span>
                </div>

                <div className="newton-login__editorial-body">
                    <span className="newton-login__product-badge">Newton Pay</span>
                    <h1 className="newton-login__headline">Take a deep breath.</h1>
                    <p className="newton-login__subcopy">
                        Sign in to Newton Pay. Invoices, ledgers and collections for your practice.
                    </p>
                </div>

                <div className="newton-login__footer" aria-hidden="true" />
            </section>

            {/* Right — form panel */}
            <section className="newton-login__form-panel">
                <div className="newton-login__card">
                    <div className="newton-login__card-head">
                        <span className="newton-login__card-eyebrow">
                            {requiresMFA ? 'Two-factor' : 'Sign in'}
                        </span>
                        {requiresMFA ? (
                            <h2 className="newton-login__card-title">Verify your identity</h2>
                        ) : (
                            // Payments shares accounts with the main app — there is
                            // no separate password to set or reset here.
                            <p className="newton-login__hint">
                                Use your Newton app email and password.
                            </p>
                        )}
                    </div>

                    {error && <p className="newton-login__error">{error}</p>}

                    <form className="newton-login__form" onSubmit={handleSubmit}>
                        {!requiresMFA && (
                            <>
                                <div className="newton-login__field">
                                    <label className="newton-login__label" htmlFor="email">Email</label>
                                    <input
                                        id="email"
                                        className="newton-login__input"
                                        type="email"
                                        placeholder="Email address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        autoComplete="email"
                                    />
                                </div>

                                <div className="newton-login__field">
                                    <label className="newton-login__label" htmlFor="password">Password</label>
                                    <div className="newton-login__input-wrap">
                                        <input
                                            id="password"
                                            className="newton-login__input newton-login__input--with-action"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            autoComplete="current-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            className="newton-login__toggle"
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                            aria-pressed={showPassword}
                                        >
                                            <span className="newton-login__toggle-icon" data-visible={!showPassword}>
                                                <EyeIcon />
                                            </span>
                                            <span className="newton-login__toggle-icon" data-visible={showPassword}>
                                                <EyeOffIcon />
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {requiresMFA && (
                            <div className="newton-login__field">
                                <div className="newton-login__mfa-banner">
                                    <span className="newton-login__mfa-banner-title">Two-factor authentication required</span>
                                    <span className="newton-login__mfa-banner-hint">
                                        Signed in as {email}. Enter the 6-digit code from your authenticator app — codes refresh every 30 seconds.
                                    </span>
                                </div>

                                <label className="newton-login__label" htmlFor="mfaCode">Authentication code</label>
                                <input
                                    id="mfaCode"
                                    className="newton-login__input newton-login__input--mfa"
                                    type="text"
                                    inputMode="numeric"
                                    value={mfaCode}
                                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder="123456"
                                    maxLength={6}
                                    required
                                    autoFocus
                                />
                            </div>
                        )}

                        <button type="submit" className="newton-login__submit" disabled={loading}>
                            {loading ? 'Verifying…' : requiresMFA ? 'Verify code  →' : 'Log in  →'}
                        </button>

                        {requiresMFA && (
                            <button
                                type="button"
                                className="newton-login__ghost"
                                onClick={() => {
                                    setRequiresMFA(false);
                                    setMfaCode('');
                                    setError('');
                                    setLoading(false);
                                }}
                            >
                                Back to login
                            </button>
                        )}
                    </form>

                    {!requiresMFA && (
                        <div className="newton-login__register">
                            <span>Don't have an account? Contact support</span>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default LoginPage;
